import {
  type ArrayType,
  type Form,
  Format,
  type FormField,
  type ObjectType,
  type StringType,
  type Validation,
} from '@wix/ambassador-forms-v4-form/types';

import {
  ErrorTypes,
  type ValidationError,
  type ValidationResult,
} from '../submission-validation/errors';
import { asRequired, notEmpty } from '../utils/lang';

import { fieldHasTarget, isInputField } from './field-utils';

type CompatibilityProblem = Pick<ValidationError, 'errorMessage' | 'params'>;

export function compareSchemaBackwardCompatibility(
  newSchema: Form,
  knownSchema: Form,
): ValidationResult {
  return {
    errors: getNonCompatibleFields(newSchema, knownSchema),
  };
}

function getNonCompatibleFields(
  newSchema: Form,
  knownSchema: Form,
): ValidationError[] {
  newSchema.fields?.forEach(isInputField); // validate target exists for all the input fields
  const newFieldsWithTarget: FormField[] = (newSchema.fields ?? []).filter(
    fieldHasTarget,
  );
  const knownInputFields: { [key: string]: FormField } = [
    ...(knownSchema.fields ?? []),
    ...(knownSchema.deletedFields ?? []),
  ]
    .filter(fieldHasTarget)
    .reduce<{ [key: string]: FormField }>((acc, field) => {
      acc[asRequired(field.target, field, 'target')] = field;
      return acc;
    }, {});

  return newFieldsWithTarget
    .map((field) =>
      fieldHasCompatibleType(
        field,
        knownInputFields[asRequired(field.target, field, 'target')],
      ),
    )
    .filter(notEmpty);
}

function fieldHasCompatibleType(
  field: FormField,
  knownField?: FormField,
): ValidationError | null {
  if (!knownField) {
    return null;
  }

  const [newValidation] = Object.entries(field.validation ?? {});
  const [knownValidation] = Object.entries(knownField.validation ?? {});

  const compatibilityProblem = confirmValidationCompatibility(
    newValidation,
    knownValidation,
  );

  if (compatibilityProblem) {
    return {
      errorPath: asRequired(field.target, field, 'target'),
      errorType: ErrorTypes.FIELDS_COMPATIBILITY_ERROR,
      errorMessage: compatibilityProblem.errorMessage,
      params: compatibilityProblem.params,
    };
  }
  return null;
}

function confirmValidationCompatibility(
  newValidation?: [string, Validation[keyof Validation]],
  currentValidation?: [string, Validation[keyof Validation]],
): CompatibilityProblem | null {
  if (!currentValidation) {
    return null;
  }

  if (!newValidation) {
    return {
      errorMessage: 'Field requires validation property',
    };
  }

  const [fieldType, fieldValidation] = newValidation;
  const [knownType, knownValidation] = currentValidation;

  if (fieldType !== knownType) {
    return {
      errorMessage: 'Incompatible field type',
      params: { current: fieldType, previous: knownType },
    };
  }

  switch (fieldType) {
    case 'string':
      return confirmStringValidationCompatible(
        fieldValidation as StringType,
        knownValidation as StringType,
      );
    case 'array':
      return confirmArrayValidationCompatible(
        fieldValidation as ArrayType,
        knownValidation as ArrayType,
      );
    case 'object':
      return confirmObjectValidationCompatible(
        fieldValidation as ObjectType,
        knownValidation as ObjectType,
      );
    default:
      return null;
  }
}

function confirmStringValidationCompatible(
  newValidation: StringType,
  knownValidation: StringType,
): CompatibilityProblem | null {
  if (
    knownValidation.format !== Format.UNDEFINED &&
    newValidation.format !== knownValidation.format
  ) {
    return {
      errorMessage: 'Incompatible string format',
      params: {
        current: newValidation.format,
        previous: knownValidation.format,
      },
    };
  }
  return null;
}

function confirmArrayValidationCompatible(
  newValidation: ArrayType,
  knownValidation: ArrayType,
): CompatibilityProblem | null {
  const [itemValidation] = Object.entries(newValidation.items ?? {});
  const [knownItemValidation] = Object.entries(knownValidation.items ?? {});
  return confirmValidationCompatibility(itemValidation, knownItemValidation);
}

function confirmObjectValidationCompatible(
  newValidation: ObjectType,
  knownValidation: ObjectType,
): CompatibilityProblem | null {
  const propertyNames = Object.keys(newValidation.properties ?? {})
    .slice()
    .sort();
  const knownPropertyNames = Object.keys(knownValidation.properties ?? {})
    .slice()
    .sort();

  if (
    knownPropertyNames.every((value, index) => value === propertyNames[index])
  ) {
    const propertyProblems: { [property: string]: CompatibilityProblem } =
      knownPropertyNames.reduce<{ [property: string]: CompatibilityProblem }>(
        (acc, name) => {
          const [propertyValidation] = Object.entries(
            (newValidation.properties ?? {})[name],
          );
          const [knownPropertyValidation] = Object.entries(
            (knownValidation.properties ?? {})[name],
          );
          const problem = confirmValidationCompatibility(
            propertyValidation,
            knownPropertyValidation,
          );
          if (problem) {
            acc[name] = problem;
          }
          return acc;
        },
        {},
      );

    if (Object.keys(propertyProblems).length) {
      return {
        errorMessage: 'Object has incompatible properties',
        params: propertyProblems,
      };
    } else {
      return null;
    }
  }
  return {
    errorMessage: 'Object has mismatching properties',
    params: { current: propertyNames, previous: knownPropertyNames },
  };
}
